import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useState } from 'react';
import { cx } from '../../lib/cx.js';
import { dismissToast, useToasts, type Toast, type ToastTone } from '../../lib/toast.js';

const TONES: Record<ToastTone, { ink: string; icon: typeof Info }> = {
  info: { ink: 'var(--accent-ink)', icon: Info },
  success: { ink: 'var(--success)', icon: CheckCircle2 },
  error: { ink: 'var(--danger)', icon: AlertCircle },
};

/** Bottom-right toast stack. Polite live region: never steals focus. */
export function Toaster() {
  const toasts = useToasts();
  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      // Clear of the phone's bottom navigation, which is 56px plus its inset.
      className="pointer-events-none fixed bottom-[calc(68px+env(safe-area-inset-bottom))] right-3 z-[60] flex w-[min(360px,calc(100vw-24px))] flex-col gap-2 lg:bottom-3"
    >
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

function ToastRow({ toast }: { toast: Toast }) {
  const [busy, setBusy] = useState(false);
  const tone = TONES[toast.tone];
  const Icon = tone.icon;
  return (
    <div
      className={cx(
        'vam-enter pointer-events-auto flex items-start gap-2.5 rounded-[var(--radius-md)]',
        'border border-[var(--border)] bg-[var(--surface)] px-3 py-2.5 shadow-[var(--shadow-2)]',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: tone.ink }} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[var(--text-base)] font-medium text-[var(--text)]">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-[var(--text-base)] text-[var(--text-2)]">{toast.description}</p>
        )}
      </div>
      {toast.action && (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void Promise.resolve(toast.action?.run()).finally(() => dismissToast(toast.id));
          }}
          className="shrink-0 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[var(--text-base)] font-medium text-[var(--accent-ink)] hover:bg-[var(--accent-soft)] disabled:opacity-50"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => dismissToast(toast.id)}
        className="shrink-0 rounded-[var(--radius-sm)] p-0.5 text-[var(--text-3)] hover:text-[var(--text)]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
